import {
  numberToCurrencyString,
  numberToDisplayString,
} from 'further-ui/utils';
import { CurrencyCode } from 'further-types/lp/vehicle';
import useFormatHoldingClassData from './useFormatHoldingClassData';
import {
  HoldingClassInvestmentsConsiderationValues,
  HoldingClassRealisations,
} from '../../types';
import Table, { Column as TableColumn } from 'further-ui/components/Table';
import ColorIndicatorWithText from 'components/new/ColorIndicatorWithText';
import { Column } from 'further-ui/components/Layout';
import { Box, styled, Typography } from '@mui/material';
import { keyBy, sumBy } from 'lodash';
import { useExpandedRows } from 'hooks/ui/useExpandedRows';

const ExpandedTableContainer = styled(Box)(({ theme }) => ({
  padding: theme.spacing(0, theme.spacingSizes.sm, theme.spacingSizes.sm),
}));

type Props = {
  holdingClassRealisations: HoldingClassRealisations;
  holdingClassInvestmentsConsiderationValues?: Array<HoldingClassInvestmentsConsiderationValues>;
  currency: CurrencyCode;
};

const PerClassAllocationTable: React.FC<Props> = ({
  holdingClassRealisations,
  holdingClassInvestmentsConsiderationValues = [],
  currency,
}) => {
  const { expandedRows, toggleRowExpansion } = useExpandedRows();

  const formattedHoldingClassRealisations = useFormatHoldingClassData(
    holdingClassRealisations,
  );

  const unitsByInvestmentId = keyBy(
    holdingClassRealisations.flatMap(
      (hc) => hc.unitsAllocationPerInvestment ?? [],
    ),
    'investmentId',
  );

  const rows = formattedHoldingClassRealisations.map((holdingClass) => ({
    id: holdingClass.holdingClassId,
    ...holdingClass,
  }));

  const totals = {
    unitsRealised: sumBy(rows, 'unitsRealised'),
    receivedConsiderationTotal: sumBy(rows, 'receivedConsiderationTotal'),
    escrowConsiderationTotal: sumBy(rows, 'escrowConsiderationTotal'),
    earnoutConsiderationTotal: sumBy(rows, 'earnoutConsiderationTotal'),
    withholdingTaxPayable: sumBy(rows, 'withholdingTaxPayable'),
    total:
      sumBy(rows, 'receivedConsiderationTotal') +
      sumBy(rows, 'escrowConsiderationTotal') +
      sumBy(rows, 'earnoutConsiderationTotal') +
      sumBy(rows, 'withholdingTaxPayable'),
  };

  const columns: Array<TableColumn<(typeof rows)[number]>> = [
    {
      key: 'label',
      label: 'Class',
      width: 200,
      render: ({ holdingClassName }) => (
        <ColorIndicatorWithText text={holdingClassName} />
      ),
      footer: 'Total',
    },
    {
      key: 'unitsHeld',
      label: 'Units held',
      width: 120,
      render: ({ unitsHeld }) => numberToDisplayString(unitsHeld),
    },
    {
      key: 'unitsRealised',
      label: 'Units realised',
      width: 120,
      render: ({ unitsRealised }) => numberToDisplayString(unitsRealised),
      footer: numberToDisplayString(totals.unitsRealised),
    },
    {
      key: 'receivedConsideration',
      label: 'Received consideration',
      width: 180,
      render: ({
        receivedConsiderationTotal,
        receivedConsiderationPerUnit,
      }) => (
        <Column>
          <Typography variant="inherit">
            {numberToCurrencyString(receivedConsiderationTotal, { currency })}
          </Typography>
          <Typography variant="body2" color="grey.600">
            {numberToCurrencyString(receivedConsiderationPerUnit, { currency })}{' '}
            per unit
          </Typography>
        </Column>
      ),
      footer: numberToCurrencyString(totals.receivedConsiderationTotal, {
        currency,
      }),
    },
    {
      key: 'escrow',
      label: 'Escrow',
      width: 160,
      render: ({ escrowConsiderationTotal, escrowConsiderationPerUnit }) => (
        <Column>
          <Typography variant="inherit">
            {numberToCurrencyString(escrowConsiderationTotal, { currency })}
          </Typography>
          <Typography variant="body2" color="grey.600">
            {numberToCurrencyString(escrowConsiderationPerUnit, { currency })}{' '}
            per unit
          </Typography>
        </Column>
      ),
      footer: numberToCurrencyString(totals.escrowConsiderationTotal, {
        currency,
      }),
    },
    {
      key: 'earnout',
      label: 'Earnout',
      width: 160,
      render: ({ earnoutConsiderationPerUnit, earnoutConsiderationTotal }) => (
        <Column>
          <Typography variant="inherit">
            {numberToCurrencyString(earnoutConsiderationTotal, { currency })}
          </Typography>
          <Typography variant="body2" color="grey.600">
            {numberToCurrencyString(earnoutConsiderationPerUnit, { currency })}{' '}
            per unit
          </Typography>
        </Column>
      ),
      footer: numberToCurrencyString(totals.earnoutConsiderationTotal, {
        currency,
      }),
    },
    {
      key: 'withholdingTaxPayable',
      label: 'Withholding tax payable',
      width: 200,
      render: ({ withholdingTaxPayable }) =>
        numberToCurrencyString(withholdingTaxPayable, { currency }),
      footer: numberToCurrencyString(totals.withholdingTaxPayable, {
        currency,
      }),
    },
    {
      key: 'total',
      label: 'Total consideration',
      width: 180,
      render: ({
        receivedConsiderationTotal,
        escrowConsiderationTotal,
        earnoutConsiderationTotal,
        withholdingTaxPayable,
      }) =>
        numberToCurrencyString(
          receivedConsiderationTotal +
            escrowConsiderationTotal +
            earnoutConsiderationTotal +
            withholdingTaxPayable,
          { currency },
        ),
      footer: numberToCurrencyString(totals.total, { currency }),
    },
  ];

  const investmentColumns: Array<
    TableColumn<HoldingClassInvestmentsConsiderationValues & { id: string }>
  > = [
    {
      key: 'investmentDate',
      label: 'Investment date',
      width: 150,
      render: ({ investmentDate }) => investmentDate?.toReadable(),
    },
    {
      key: 'unitsHeld',
      label: 'Units held',
      width: 100,
      render: ({ investmentId }) =>
        numberToDisplayString(
          unitsByInvestmentId[investmentId ?? '']?.currentNumberOfUnits ?? 0,
        ),
    },
    {
      key: 'unitsRealised',
      label: 'Units realised',
      width: 120,
      render: ({ unitsRealised }) => numberToDisplayString(unitsRealised),
    },
    {
      key: 'receivedConsideration',
      label: 'Received consideration',
      width: 180,
      render: ({
        receivedConsiderationTotal,
        receivedConsiderationPerUnit,
      }) => (
        <Column>
          <Typography variant="inherit">
            {numberToCurrencyString(receivedConsiderationTotal, { currency })}
          </Typography>
          <Typography variant="body2" color="grey.600">
            {numberToCurrencyString(receivedConsiderationPerUnit, {
              currency,
            })}{' '}
            per unit
          </Typography>
        </Column>
      ),
    },
    {
      key: 'escrow',
      label: 'Escrow',
      width: 150,
      render: ({ escrowConsiderationTotal, escrowConsiderationPerUnit }) => (
        <Column>
          <Typography variant="inherit">
            {numberToCurrencyString(escrowConsiderationTotal, { currency })}
          </Typography>
          <Typography variant="body2" color="grey.600">
            {numberToCurrencyString(escrowConsiderationPerUnit, { currency })}{' '}
            per unit
          </Typography>
        </Column>
      ),
    },
    {
      key: 'earnout',
      label: 'Earnout',
      width: 150,
      render: ({ earnoutConsiderationTotal, earnoutConsiderationPerUnit }) => (
        <Column>
          <Typography variant="inherit">
            {numberToCurrencyString(earnoutConsiderationTotal, { currency })}
          </Typography>
          <Typography variant="body2" color="grey.600">
            {numberToCurrencyString(earnoutConsiderationPerUnit, { currency })}{' '}
            per unit
          </Typography>
        </Column>
      ),
    },
    {
      key: 'withholdingTaxPayable',
      label: 'Withholding tax payable',
      width: 190,
      render: ({ withholdingTaxPayable }) =>
        numberToCurrencyString(withholdingTaxPayable, { currency }),
    },
    {
      key: 'total',
      label: 'Total consideration',
      width: 170,
      render: ({
        receivedConsiderationTotal,
        escrowConsiderationTotal,
        earnoutConsiderationTotal,
        withholdingTaxPayable,
      }) =>
        numberToCurrencyString(
          receivedConsiderationTotal +
            escrowConsiderationTotal +
            earnoutConsiderationTotal +
            withholdingTaxPayable,
          { currency },
        ),
    },
  ];

  return (
    <Table
      columns={columns}
      scrollable
      rows={rows}
      isExpandableRow={(row) =>
        holdingClassInvestmentsConsiderationValues.some(
          (hc) => hc.holdingClassId === row.id,
        )
      }
      isExpandedRow={(row) => expandedRows.has(row.id)}
      onRowExpand={(row) => toggleRowExpansion(row.id)}
      onRowClick={
        holdingClassInvestmentsConsiderationValues.length > 0
          ? (row) => toggleRowExpansion(row.id)
          : undefined
      }
      getExpandedContent={(row) => {
        const investments = holdingClassInvestmentsConsiderationValues
          .filter((hc) => hc.holdingClassId === row.id)
          .map((inv) => ({ id: inv.investmentId ?? '', ...inv }));
        return (
          <ExpandedTableContainer>
            <Table
              showHeaderBorder
              showTopBorder
              scrollable
              columns={investmentColumns}
              rows={investments}
            />
          </ExpandedTableContainer>
        );
      }}
    />
  );
};

export default PerClassAllocationTable;
